import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Trash, Warning, TrendUp, TrendDown, ChartLineUp, Wallet, PencilSimple, FilePdf, FileXls, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_LABELS = {
  title: "Title", asset_id: "Asset", lease_type: "Lease Type", party_role: "Role",
  start_date: "Start Date", end_date: "End Date", rent_amount: "Rent Amount", frequency: "Frequency",
  currency: "Currency", discount_rate: "Discount Rate", advance_payment: "Advance Payment",
  payment_timing: "Payment Timing", is_low_value: "Low-value Flag", lessor_classification: "Lessor Class",
  notes: "Notes",
};

function fmtVal(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function Stat({ label, value, tone }) {
  const cls = tone === "pos" ? "text-[hsl(var(--success))]" : tone === "neg" ? "text-destructive" : "text-primary";
  return (
    <div className="p-4 border border-border rounded-sm bg-card">
      <div className="text-[10px] font-medium tracking-[0.15em] uppercase text-muted-foreground mb-1.5">{label}</div>
      <div className={`font-mono text-lg font-bold ${cls}`}>{value}</div>
    </div>
  );
}

export default function LeaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [history, setHistory] = useState([]);
  const [journal, setJournal] = useState(null);

  const load = useCallback(() => api.get(`/leases/${id}`).then((r) => setL(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/leases/${id}/history`).then((r) => setHistory(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get(`/leases/${id}/journal`).then((r) => setJournal(r.data)).catch(() => {}); }, [id]);

  const del = async () => {
    try { await api.delete(`/leases/${id}`); toast.success("Lease deleted"); nav("/leases"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/leases/${id}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `IFRS16-Lease-${(l?.title || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not generate report");
    }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/leases/${id}/report.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `IFRS16-Lease-${(l?.title || "report").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Workbook downloaded");
    } catch {
      toast.error("Could not generate workbook");
    }
  };

  if (!l) return <div className="text-muted-foreground text-sm">Loading…</div>;
  const c = l.currency;
  const ifrs = l.ifrs;

  return (
    <div className="space-y-8">
      <button onClick={() => nav("/leases")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> Back to leases
      </button>

      <div className="flex items-start justify-between fade-up">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="rounded-sm">{l.lease_type}</Badge>
            <Badge variant="outline" className={`rounded-sm ${l.lease_nature === "Revenue" ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40" : "text-amber-600 border-amber-600/40"}`}>{l.lease_nature || "Expense"} Lease</Badge>
            <Badge variant="outline" className="rounded-sm">{l.party_role}</Badge>
            {l.party_role === "Lessor" && <Badge variant="outline" className="rounded-sm">{l.lessor_classification} Lease</Badge>}
            {ifrs.exempt && <Badge variant="outline" className="rounded-sm gap-1 text-amber-600 border-amber-600/40"><Warning size={12} /> Exempt</Badge>}
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">{l.title}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            {l.asset?.asset_number} · {l.asset?.name} · {l.start_date} → {l.end_date} · {ifrs.term_months} months
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadReport} className="rounded-sm gap-2" data-testid="download-report-btn"><FilePdf size={16} /> IFRS 16 Report</Button>
          <Button onClick={downloadXlsx} variant="outline" className="rounded-sm gap-2" data-testid="download-xlsx-btn"><FileXls size={16} /> Excel</Button>
          <Button variant="outline" onClick={() => nav(`/leases?edit=${id}`)} className="rounded-sm gap-2" data-testid="edit-lease-btn"><PencilSimple size={16} /> Edit</Button>
          <Button variant="outline" onClick={del} className="rounded-sm gap-2 text-destructive hover:text-destructive" data-testid="delete-lease-btn"><Trash size={16} /> Delete</Button>
        </div>
      </div>

      {ifrs.exempt ? (
        <Card className="rounded-sm border-amber-500/30 bg-amber-500/5 shadow-none p-6 fade-up">
          <div className="flex items-start gap-3">
            <Warning size={22} className="text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-display font-semibold tracking-tight">IFRS 16 Exemption Applied</h3>
              <p className="text-sm text-muted-foreground mt-1">
                This is a {ifrs.is_short_term ? "short-term (≤12 months)" : "low-value"} lease. No ROU asset or lease liability is recognised.
                Payments are expensed straight to the income statement on a straight-line basis.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                <Stat label="Total Payments" value={money(ifrs.total_payments, c)} tone="neg" />
                <Stat label="Periodic Expense" value={money(ifrs.straight_line_expense, c)} tone="neg" />
                <Stat label="Periods" value={ifrs.num_periods} tone="pri" />
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 fade-up">
          <Stat label="ROU Asset" value={money(ifrs.rou_asset, c)} tone="pos" />
          <Stat label="Lease Liability" value={money(ifrs.lease_liability, c)} tone="neg" />
          <Stat label="Total Interest" value={money(ifrs.total_interest, c)} tone="neg" />
          <Stat label="Total Depreciation" value={money(ifrs.total_depreciation, c)} tone="neg" />
          <Stat label="Total Payments" value={money(ifrs.total_payments, c)} tone="pri" />
          <Stat label="Period Rate" value={`${ifrs.period_rate_pct}%`} tone="pri" />
        </div>
      )}

      <Tabs defaultValue="amortization" className="fade-up">
        <TabsList className="rounded-sm">
          <TabsTrigger value="amortization" className="rounded-sm" data-testid="tab-amortization">Amortization</TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-sm" data-testid="tab-schedule">Rent Schedule</TabsTrigger>
          <TabsTrigger value="journal" className="rounded-sm" data-testid="tab-journal">Journal Entries</TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-sm" data-testid="tab-expenses">Expenses ({l.expenses?.length || 0})</TabsTrigger>
          <TabsTrigger value="history" className="rounded-sm" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="amortization" className="mt-4">
          {ifrs.schedule.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">No amortization schedule (exempt lease).</p>
          ) : (
            <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>#</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Opening Liability</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right">Closing Liability</TableHead>
                      <TableHead className="text-right">Depreciation</TableHead>
                      <TableHead className="text-right">ROU Carrying</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ifrs.schedule.map((s) => (
                      <TableRow key={s.period} data-testid={`amort-row-${s.period}`}>
                        <TableCell className="font-mono text-muted-foreground">{s.period}</TableCell>
                        <TableCell className="font-mono text-xs">{s.date}</TableCell>
                        <TableCell className="text-right font-mono">{money(s.opening_liability, c)}</TableCell>
                        <TableCell className="text-right font-mono text-destructive">{money(s.interest, c)}</TableCell>
                        <TableCell className="text-right font-mono">{money(s.payment, c)}</TableCell>
                        <TableCell className="text-right font-mono">{money(s.closing_liability, c)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{money(s.depreciation, c)}</TableCell>
                        <TableCell className="text-right font-mono text-[hsl(var(--success))]">{money(s.rou_carrying, c)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Period</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ifrs.schedule.length ? ifrs.schedule : []).map((s) => (
                    <TableRow key={s.period}>
                      <TableCell className="font-mono text-muted-foreground">{s.period}</TableCell>
                      <TableCell className="font-mono text-xs">{s.date}</TableCell>
                      <TableCell className="text-right font-mono">{money(s.payment, c)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          {!journal || journal.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">No journal entries.</p>
          ) : (
            <div className="space-y-3" data-testid="journal-list">
              <p className="text-xs text-muted-foreground">
                IFRS 16 double-entry postings ({journal.party_role}{journal.exempt ? ", exempt" : ""}). Amounts in {c}.
              </p>
              {journal.entries.map((e, idx) => (
                <Card key={idx} className="rounded-sm border-border shadow-none overflow-hidden bg-card" data-testid={`journal-entry-${e.period}`}>
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
                    <Badge variant={e.period === 0 ? "default" : "secondary"} className="rounded-sm font-mono">
                      {e.period === 0 ? "Commencement" : `Period ${e.period}`}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">{e.date}</span>
                    <span className="text-xs text-muted-foreground ml-2 truncate">{e.description}</span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {e.lines.map((ln, i) => (
                        <TableRow key={i}>
                          <TableCell className={ln.credit > 0 ? "pl-8 text-muted-foreground" : "font-medium"}>{ln.account}</TableCell>
                          <TableCell className="text-right font-mono">{ln.debit > 0 ? money(ln.debit, c) : ""}</TableCell>
                          <TableCell className="text-right font-mono">{ln.credit > 0 ? money(ln.credit, c) : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          {(!l.expenses || l.expenses.length === 0) ? (
            <p className="text-sm text-muted-foreground p-6">No one-time expenses linked. Add them from the Expenses page.</p>
          ) : (
            <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {l.expenses.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell><Badge variant="secondary" className="rounded-sm">{e.expense_type}</Badge></TableCell>
                      <TableCell>{e.description || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{e.date}</TableCell>
                      <TableCell className="text-right font-mono">{money(e.amount, e.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">No change history yet.</p>
          ) : (
            <div className="space-y-3" data-testid="history-list">
              {history.map((h) => (
                <Card key={h.id} className="rounded-sm border-border shadow-none p-5 bg-card">
                  <div className="flex items-center gap-2 mb-3">
                    <ClockCounterClockwise size={16} className="text-primary" />
                    <Badge variant={h.action === "created" ? "default" : "secondary"} className="rounded-sm capitalize">{h.action}</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{new Date(h.at).toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-auto">by {h.user}</span>
                  </div>
                  {h.action === "created" ? (
                    <p className="text-sm text-muted-foreground">Lease contract created.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {h.changes.map((ch, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`history-change-${ch.field}`}>
                          <span className="font-medium w-36 shrink-0">{FIELD_LABELS[ch.field] || ch.field}</span>
                          <span className="font-mono text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-sm line-through">{fmtVal(ch.old)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono text-xs px-2 py-0.5 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] rounded-sm">{fmtVal(ch.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {l.notes && (
        <Card className="rounded-sm border-border shadow-none p-6 bg-card fade-up">
          <h3 className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Notes</h3>
          <p className="text-sm">{l.notes}</p>
        </Card>
      )}
    </div>
  );
}
